import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Tab, Tabs, Typography } from '@mui/material'
import InvalidEmployeeTable from '../employee/InvalidEmployeeTable'
import InvalidEmployeeDeleteTable, { InvalidEmployeeDeleteTableRef } from '../employee/InvalidEmployeeDeleteTable'
import { IImportError, IImportErrorDelete, useEmployeeImportDeleteMutation } from '@/@data'
import { useNotification } from '@/hooks'
import { useTranslator } from '@/hooks/useTranslator'
import React, { useState, useRef } from 'react'

export interface SimpleDialogProps {
  open: boolean
  data: IImportError[]
  dataDelete: IImportErrorDelete[]
  onClose: (value: boolean) => void
  onFileReset?: () => void
  refetch?: () => void
}

export function InvalidEmployeeImportDialog(props: SimpleDialogProps) {
  const { __ } = useTranslator()
  const { onClose, data, open, onFileReset, refetch, dataDelete } = props
  const tableRef = useRef<HTMLTableElement>(null)
  const deleteTableRef = useRef<InvalidEmployeeDeleteTableRef>(null)
  const [tabValue, setTabValue] = useState(0)
  const n = useNotification()
  const { mutate: importEmployeeDelete, isPending } = useEmployeeImportDeleteMutation()

  const handleClose = () => {
    onClose(false)
    onFileReset?.()
    refetch?.()
  }

  const handleCopy = async () => {
    if (tableRef.current) {
      const html = tableRef.current.outerHTML

      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([html], { type: 'text/plain' })
        })
      ])

      alert(__('Copied table as HTML! Paste into Excel now.'))
    }
  }

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const handleConfirmCreateUser = () => {
    const checkedUserIds = deleteTableRef.current?.getCheckedUserIds() || []

    if (checkedUserIds.length === 0) {
      n.warning(__('Please select at least one user to create'))

      return
    }

    importEmployeeDelete(
      { data: { userIds: checkedUserIds } },
      {
        onSuccess: () => {
          n.success(__('Users created successfully'))
          handleClose()
        },
        onError: error => {
          n.error(error?.message ? __(error.message) : __('Failed to create users'))
        }
      }
    )
  }

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      sx={{
        '& .MuiDialog-paper': { overflow: 'visible', minWidth: '900px' }
      }}
    >
      <DialogTitle>{__('Invalid Template File')}</DialogTitle>
      <DialogContent>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label='employer creation tabs' className='mb-4'>
          <Tab label={__('Error')} className='w-1/2' />
          <Tab label={__('User Deleted')} className='w-1/2' />
        </Tabs>

        {tabValue === 0 && <InvalidEmployeeTable data={data} ref={tableRef} />}
        {tabValue === 1 && (
          <>
            {dataDelete.length > 0 && (
              <>
                <InvalidEmployeeDeleteTable data={dataDelete} ref={deleteTableRef} />
                <Typography className='text-[15px] mt-4 text-[#FF4C51] leading-[22px]'>
                  {__('Warning: This user has requested account deletion. Are you sure you want to create a new user?')}
                </Typography>
              </>
            )}

            {dataDelete.length === 0 && (
              <Typography className='text-[15px] mt-4 text-[#000000] leading-[22px]'>
                {__('No user has requested account deletion')}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {tabValue === 0 && (
          <>
            <Button variant='outlined' className='rounded-full' onClick={handleClose} autoFocus>
              {__('Cancel')}
            </Button>
            <Button variant='contained' onClick={handleCopy} className='bg-[#3551F2] text-[#fff] rounded-full'>
              {__('Copy All Errors')}
            </Button>
          </>
        )}

        {tabValue === 1 && (
          <>
            {dataDelete.length > 0 && (
              <>
                <Button className='rounded-full' variant='outlined' onClick={handleClose} disabled={isPending}>
                  {__('Cancel')}
                </Button>
                <Button
                  onClick={handleConfirmCreateUser}
                  startIcon={<i className='tabler-checks' />}
                  variant='contained'
                  className='bg-[#28C76F] text-[#fff] rounded-full'
                  disabled={isPending}
                >
                  {__('Confirm Create User')}
                </Button>
              </>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
